import { supabase, NotificacionBalance, Movimiento, EstadoNotificacionBalance, TipoEntidadBalance } from '../lib/supabase';
import { notificacionesService } from './notificacionesService';
import { comprobanteService } from './comprobanteService';
import { SELECT_MOVIMIENTO_CON_COMPROBANTES } from './movimientoService';
import type { ObjecionComprobante } from '../types/comprobante';
import { calcularTotales } from '../utils/balanceCalculations';
import { fechaInicioMes, fechaFinMes } from '../utils/dateHelpers';
import { reportarErrorInesperado } from '../utils/reportarErrorInesperado';

export interface NotificacionBalanceDetalle extends NotificacionBalance {
  entidad_nombre: string;
  movimientos_balance: Movimiento[];      // Solo afecta_balance = true (para totales)
  movimientos_no_balance: Movimiento[];   // Solo afecta_balance = false (campañas, cuota iglesia, etc.)
  total_ingresos: number;                 // Calculado solo con afecta_balance = true
  total_egresos: number;                  // Calculado solo con afecta_balance = true
  total_financiamiento: number;           // Entradas por financiamiento de deudas: fuera de los ingresos, dentro del balance
  balance_neto: number;                   // total_ingresos + total_financiamiento - total_egresos
}

export interface NotificacionBalanceConDetalle extends NotificacionBalance {
  entidad_nombre: string;
  total_ingresos: number;
  total_egresos: number;
  /** Entradas por financiamiento de deudas: fuera de los ingresos, dentro del balance. */
  total_financiamiento: number;
  balance_neto: number;
  /** Cuota iglesia de movimientos del mes que la entidad aún debe traspasar a la junta superior. */
  cuota_iglesia_por_traspasar: number;
}

/** Notificación con el resumen del comprobante de la transferencia de cuota iglesia, para abrirlo al revisar. */
const SELECT_NOTIFICACION_CON_COMPROBANTE =
  '*, comprobante_transferencia:comprobantes!notificaciones_balance_comprobante_transferencia_id_fkey(id, tipo_archivo, rutas, nombre_original)';

export const notificacionBalanceService = {
  /**
   * Crear notificación de envío de balance.
   * Crea registro en notificaciones_balance con estado 'enviado'
   * y envía notificación push al admin del nivel superior.
   */
  async enviarNotificacion(payload: {
    tipo_entidad: TipoEntidadBalance;
    entidad_id: string;
    periodo_mes: number;
    periodo_anio: number;
    nota?: string;
    enviado_por_usuario_id: string;
    comprobante_transferencia_id?: string | null;
  }): Promise<NotificacionBalance> {
    // Validar que el período ya haya finalizado
    const fechaActual = new Date();
    const primerDiaMesSiguiente = new Date(payload.periodo_anio, payload.periodo_mes, 1);
    
    if (fechaActual < primerDiaMesSiguiente) {
      throw new Error('No se puede notificar un balance de un mes que aún no ha finalizado');
    }

    // Verificar si ya existe una notificación para esta entidad y período
    const existente = await this.getByEntidadYPeriodo(
      payload.entidad_id,
      payload.periodo_mes,
      payload.periodo_anio
    );

    if (existente) {
      throw new Error('Ya existe una notificación de balance para este período');
    }

    // Crear notificación de balance
    const { data, error } = await supabase
      .from('notificaciones_balance')
      .insert({
        tipo_entidad: payload.tipo_entidad,
        entidad_id: payload.entidad_id,
        periodo_mes: payload.periodo_mes,
        periodo_anio: payload.periodo_anio,
        estado: 'enviado' as EstadoNotificacionBalance,
        nota: payload.nota,
        enviado_por_usuario_id: payload.enviado_por_usuario_id,
        comprobante_transferencia_id: payload.comprobante_transferencia_id ?? null,
        fecha_envio: new Date().toISOString(),
      })
      .select()
      .single();

    if (error) throw error;

    // Obtener información de la entidad para el mensaje
    const entidadNombre = await this.getEntidadNombre(payload.tipo_entidad, payload.entidad_id);

    // Determinar el admin del nivel superior y enviar notificación push
    const adminSuperiorId = await this.getAdminNivelSuperior(payload.tipo_entidad, payload.entidad_id);
    
    if (adminSuperiorId) {
      await notificacionesService.crear({
        usuario_id: adminSuperiorId,
        tipo: 'balance_enviado',
        titulo: 'Balance mensual recibido',
        cuerpo: `${entidadNombre} ha enviado su balance de ${this.getNombreMes(payload.periodo_mes)} ${payload.periodo_anio}`,
        referencia_id: data.id,
        referencia_tipo: 'notificacion_balance',
      });
    }

    return data;
  },

  /**
   * Obtener notificación por ID.
   */
  async getById(id: string): Promise<NotificacionBalance | null> {
    const { data, error } = await supabase
      .from('notificaciones_balance')
      .select('*')
      .eq('id', id)
      .maybeSingle();

    if (error) throw error;
    return data;
  },

  /**
   * Obtener notificación existente para entidad+período.
   */
  async getByEntidadYPeriodo(
    entidadId: string,
    mes: number,
    anio: number
  ): Promise<NotificacionBalance | null> {
    const { data, error } = await supabase
      .from('notificaciones_balance')
      .select('*')
      .eq('entidad_id', entidadId)
      .eq('periodo_mes', mes)
      .eq('periodo_anio', anio)
      .maybeSingle();

    if (error) throw error;
    return data;
  },

  /**
   * Listar notificaciones recibidas (para admin diocesano/nacional).
   * Filtra por jurisdicción y ordena por fecha descendente.
   */
  async getNotificacionesRecibidas(filtros: {
    diocesisId?: string;
    provinciaEclesiasticaId?: string;
    estado?: EstadoNotificacionBalance;
  }): Promise<NotificacionBalanceConDetalle[]> {
    // Obtener IDs de entidades en la jurisdicción
    const entidadIds = await this.getEntidadesEnJurisdiccion(filtros);

    if (entidadIds.length === 0) {
      return [];
    }

    let query = supabase
      .from('notificaciones_balance')
      .select(SELECT_NOTIFICACION_CON_COMPROBANTE)
      .in('entidad_id', entidadIds)
      .order('fecha_envio', { ascending: false });

    if (filtros.estado) {
      query = query.eq('estado', filtros.estado);
    }

    const { data, error } = await query;
    if (error) throw error;

    // Supabase tipa el embebido del comprobante como arreglo genérico; el shape real es NotificacionBalance.
    const notificaciones = (data ?? []) as unknown as NotificacionBalance[];

    // Enriquecer con detalles
    const notificacionesConDetalle: NotificacionBalanceConDetalle[] = [];

    // deuda: N+1 consultas por notificación (nombre, totales y cuota por traspasar). techo: decenas de
    // notificaciones por pantalla. disparador: la lista tarda más de 2 s → mover totales y cuota a una
    // RPC que devuelva todo junto.
    for (const notif of notificaciones) {
      const [entidadNombre, totales, cuota] = await Promise.all([
        this.getEntidadNombre(notif.tipo_entidad, notif.entidad_id),
        this.calcularTotalesBalance(notif.tipo_entidad, notif.entidad_id, notif.periodo_mes, notif.periodo_anio),
        comprobanteService.getCuotaIglesiaPorTraspasar(
          notif.tipo_entidad,
          notif.entidad_id,
          notif.periodo_mes,
          notif.periodo_anio
        ),
      ]);

      notificacionesConDetalle.push({
        ...notif,
        entidad_nombre: entidadNombre,
        ...totales,
        cuota_iglesia_por_traspasar: cuota,
      });
    }

    return notificacionesConDetalle;
  },

  /**
   * Obtener detalle completo de una notificación (TODOS los movimientos del período).
   * Usado cuando el admin diocesano/nacional abre el detalle de una notificación recibida.
   * Retorna todos los movimientos agrupados por si afectan o no el balance.
   */
  async getDetalleNotificacion(notificacionId: string): Promise<NotificacionBalanceDetalle> {
    const { data, error } = await supabase
      .from('notificaciones_balance')
      .select(SELECT_NOTIFICACION_CON_COMPROBANTE)
      .eq('id', notificacionId)
      .single();

    if (error) throw error;
    const notif = data as unknown as NotificacionBalance;

    const entidadNombre = await this.getEntidadNombre(notif.tipo_entidad, notif.entidad_id);

    // Obtener TODOS los movimientos del período
    const { movimientos_balance, movimientos_no_balance } = await this.getMovimientosCompletosEntidad(
      notif.tipo_entidad,
      notif.entidad_id,
      notif.periodo_mes,
      notif.periodo_anio
    );

    // Calcular totales solo con movimientos que afectan balance
    const { ingresos, egresos, financiamiento, balance } = calcularTotales(movimientos_balance);

    return {
      ...notif,
      entidad_nombre: entidadNombre,
      movimientos_balance,
      movimientos_no_balance,
      total_ingresos: ingresos,
      total_egresos: egresos,
      total_financiamiento: financiamiento,
      balance_neto: balance,
    };
  },

  /**
   * Observar la rendición, con o sin objeciones de comprobantes puntuales. Siempre por la operación de
   * la base: observa y objeta en una sola transacción, y la autorización (quien puede recibir el
   * balance) la hace ella. El usuario sale de la sesión.
   * Después envía la notificación push al remitente.
   */
  async marcarObservada(
    id: string,
    nota: string,
    objeciones: readonly ObjecionComprobante[] = []
  ): Promise<NotificacionBalance> {
    const { error } = await supabase.rpc('observar_balance_con_objeciones', {
      p_notificacion_id: id,
      p_nota: nota,
      p_objeciones: objeciones.map((o) => ({
        movimiento_id: o.movimientoId,
        respaldo: o.respaldo,
        motivo: o.motivo,
      })),
    });
    if (error) throw error;

    const data = await this.getById(id);
    if (!data) throw new Error('La rendición observada no se encontró');

    // Enviar notificación push al remitente
    const entidadNombre = await this.getEntidadNombre(data.tipo_entidad, data.entidad_id);

    await notificacionesService.crear({
      usuario_id: data.enviado_por_usuario_id,
      tipo: 'balance_observado',
      titulo: 'Balance con observaciones',
      cuerpo: `Su balance de ${this.getNombreMes(data.periodo_mes)} ${data.periodo_anio} para ${entidadNombre} tiene observaciones: ${nota}`,
      referencia_id: id,
      referencia_tipo: 'notificacion_balance',
    });

    return data;
  },

  /**
   * Re-enviar balance corregido tras una observación.
   * Solo válido cuando estado === 'observado'.
   * Resetea el registro a 'enviado' y notifica al admin del nivel superior.
   */
  async reenviarNotificacion(
    id: string,
    enviado_por_usuario_id: string,
    nota?: string,
    comprobanteTransferenciaId?: string | null
  ): Promise<NotificacionBalance> {
    const { data, error } = await supabase
      .from('notificaciones_balance')
      .update({
        estado: 'enviado' as EstadoNotificacionBalance,
        nota: nota ?? null,
        // Solo si viene: sin comprobante nuevo se conserva el anterior.
        ...(comprobanteTransferenciaId === undefined ? {} : { comprobante_transferencia_id: comprobanteTransferenciaId }),
        nota_observacion: null,
        recibido_por_usuario_id: null,
        enviado_por_usuario_id,
        fecha_envio: new Date().toISOString(),
        fecha_actualizacion: new Date().toISOString(),
      })
      .eq('id', id)
      .eq('estado', 'observado')
      .select()
      .single();

    if (error) throw error;

    // Notificar al admin del nivel superior
    const entidadNombre = await this.getEntidadNombre(data.tipo_entidad, data.entidad_id);
    const adminSuperiorId = await this.getAdminNivelSuperior(data.tipo_entidad, data.entidad_id);

    if (adminSuperiorId) {
      await notificacionesService.crear({
        usuario_id: adminSuperiorId,
        tipo: 'balance_enviado',
        titulo: 'Balance corregido recibido',
        cuerpo: `${entidadNombre} ha reenvido su balance de ${this.getNombreMes(data.periodo_mes)} ${data.periodo_anio} con correcciones`,
        referencia_id: id,
        referencia_tipo: 'notificacion_balance',
      });
    }

    return data;
  },

  /**
   * Contar notificaciones pendientes de revisión (estado 'enviado').
   */
  async contarPendientes(filtros: {
    diocesisId?: string;
    provinciaEclesiasticaId?: string;
  }): Promise<number> {
    const entidadIds = await this.getEntidadesEnJurisdiccion(filtros);

    if (entidadIds.length === 0) {
      return 0;
    }

    const { count, error } = await supabase
      .from('notificaciones_balance')
      .select('*', { count: 'exact', head: true })
      .in('entidad_id', entidadIds)
      .eq('estado', 'enviado');

    if (error) throw error;
    return count || 0;
  },

  /**
   * Historial de notificaciones enviadas por una entidad.
   */
  async getHistorialPorEntidad(entidadId: string): Promise<NotificacionBalance[]> {
    const { data, error } = await supabase
      .from('notificaciones_balance')
      .select('*')
      .eq('entidad_id', entidadId)
      .order('periodo_anio', { ascending: false })
      .order('periodo_mes', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  // --- Funciones auxiliares ---

  /**
   * Obtener nombre de la entidad (lumisial o diócesis).
   */
  async getEntidadNombre(tipoEntidad: TipoEntidadBalance, entidadId: string): Promise<string> {
    if (tipoEntidad === 'lumisial') {
      const { data, error } = await supabase
        .from('lumisiales')
        .select('nombre')
        .eq('id', entidadId)
        .single();

      if (error) throw error;
      return data.nombre;
    } else {
      const { data, error } = await supabase
        .from('diocesis')
        .select('nombre')
        .eq('id', entidadId)
        .single();

      if (error) throw error;
      return data.nombre;
    }
  },

  /**
   * Obtener ID del admin del nivel superior.
   * Para lumisial → admin diocesano de su diócesis.
   * Para diócesis → admin nacional de su provincia eclesiástica.
   */
  async getAdminNivelSuperior(tipoEntidad: TipoEntidadBalance, entidadId: string): Promise<string | null> {
    if (tipoEntidad === 'lumisial') {
      const { data: lumisial, error: lumisialError } = await supabase
        .from('lumisiales')
        .select('diocesis_id')
        .eq('id', entidadId)
        .single();
      if (lumisialError) throw lumisialError;

      const { data: lumisiales, error: lumError } = await supabase
        .from('lumisiales')
        .select('id')
        .eq('diocesis_id', lumisial.diocesis_id)
        .eq('activo', true);
      if (lumError) throw lumError;

      return this.getAdminUserIdEnLumisiales((lumisiales || []).map(l => l.id), 'admin_diocesano');
    } else {
      const { data: diocesis, error: diocesisError } = await supabase
        .from('diocesis')
        .select('provincia_eclesiastica_id')
        .eq('id', entidadId)
        .single();
      if (diocesisError) throw diocesisError;

      const { data: lumisiales, error: lumError } = await supabase
        .from('lumisiales')
        .select('id')
        .eq('provincia_eclesiastica_id', diocesis.provincia_eclesiastica_id)
        .eq('activo', true);
      if (lumError) throw lumError;

      return this.getAdminUserIdEnLumisiales((lumisiales || []).map(l => l.id), 'admin_nacional');
    }
  },

  /**
   * Buscar el user_id del primer miembro activo con el rol dado dentro de los lumisiales indicados.
   */
  async getAdminUserIdEnLumisiales(lumisialIds: string[], rolNombre: string): Promise<string | null> {
    if (lumisialIds.length === 0) return null;
    const rolIds = await this.getRolIds([rolNombre]);
    const { data: admin, error } = await supabase
      .from('miembros')
      .select('user_id')
      .in('lumisial_actual_id', lumisialIds)
      .eq('estado_miembro', 'activo')
      .in('rol_id', rolIds)
      .not('user_id', 'is', null)
      .maybeSingle();
    if (error) throw error;
    return (admin?.user_id as string | null) ?? null;
  },

  /**
   * Obtener IDs de roles por nombre.
   */
  async getRolIds(nombres: string[]): Promise<string[]> {
    const { data, error } = await supabase
      .from('roles')
      .select('id')
      .in('nombre', nombres);

    if (error) throw error;
    return (data || []).map(r => r.id);
  },

  /**
   * Obtener IDs de entidades en la jurisdicción del admin.
   */
  async getEntidadesEnJurisdiccion(filtros: {
    diocesisId?: string;
    provinciaEclesiasticaId?: string;
  }): Promise<string[]> {
    if (filtros.diocesisId) {
      // Admin diocesano: lumisiales de su diócesis
      const { data, error } = await supabase
        .from('lumisiales')
        .select('id')
        .eq('diocesis_id', filtros.diocesisId)
        .eq('activo', true);

      if (error) throw error;
      return (data || []).map(l => l.id);
    } else if (filtros.provinciaEclesiasticaId) {
      // Admin nacional: diócesis de su provincia
      const { data, error } = await supabase
        .from('diocesis')
        .select('id')
        .eq('provincia_eclesiastica_id', filtros.provinciaEclesiasticaId)
        .eq('activo', true);

      if (error) throw error;
      return (data || []).map(d => d.id);
    }

    return [];
  },

  /**
   * Calcular totales de balance para una entidad en un período.
   * Solo considera movimientos con afecta_balance = true.
   */
  async calcularTotalesBalance(
    tipoEntidad: TipoEntidadBalance,
    entidadId: string,
    mes: number,
    anio: number
  ): Promise<{ total_ingresos: number; total_egresos: number; total_financiamiento: number; balance_neto: number }> {
    const fechaInicio = fechaInicioMes(mes, anio);
    const fechaFin = fechaFinMes(mes, anio);

    const columnaEntidad = tipoEntidad === 'lumisial' ? 'lumisial_id' : 'diocesis_id';

    const { data, error } = await supabase
      .from('movimientos')
      .select('tipo, monto, origen_tipo')
      .eq(columnaEntidad, entidadId)
      .gte('fecha', fechaInicio)
      .lte('fecha', fechaFin)
      .eq('afecta_balance', true)
      .eq('anulado', false);

    if (error) throw error;

    const { ingresos, egresos, financiamiento, balance } = calcularTotales(data || []);

    return {
      total_ingresos: ingresos,
      total_egresos: egresos,
      total_financiamiento: financiamiento,
      balance_neto: balance,
    };
  },

  /**
   * Obtener TODOS los movimientos de una entidad en un período (afecta_balance true Y false).
   * Usado para la vista de detalle de notificación de balance.
   */
  async getMovimientosCompletosEntidad(
    tipoEntidad: TipoEntidadBalance,
    entidadId: string,
    mes: number,
    anio: number
  ): Promise<{ movimientos_balance: Movimiento[]; movimientos_no_balance: Movimiento[] }> {
    const fechaInicio = fechaInicioMes(mes, anio);
    const fechaFin = fechaFinMes(mes, anio);

    const columnaEntidad = tipoEntidad === 'lumisial' ? 'lumisial_id' : 'diocesis_id';

    const { data, error } = await supabase
      .from('movimientos')
      .select(SELECT_MOVIMIENTO_CON_COMPROBANTES)
      .eq(columnaEntidad, entidadId)
      .gte('fecha', fechaInicio)
      .lte('fecha', fechaFin)
      .eq('anulado', false)
      .order('fecha', { ascending: false });

    if (error) throw error;

    // La constante del select no es literal, así que Supabase no puede inferir el tipo de la fila.
    const movimientos = (data ?? []) as unknown as Movimiento[];
    const movimientos_balance = movimientos.filter(m => m.afecta_balance === true);
    const movimientos_no_balance = movimientos.filter(m => m.afecta_balance === false);

    return { movimientos_balance, movimientos_no_balance };
  },

  /**
   * Marcar notificación como recibida y registrar la transferencia de cuota iglesia.
   * La RPC `recibir_balance_con_cuota_iglesia` valida al receptor, calcula los montos en la base
   * y registra estado y asientos en una sola transacción; repetirla no duplica asientos.
   */
  async marcarRecibidaConCuotaIglesia(
    id: string
  ): Promise<{
    notificacion: NotificacionBalance;
    montoAJAL: number;
    montoPA: number;
    montoRegular: number;
    montoPAConfirmados: number;
    movimientosCreados: boolean;
  }> {
    const { data, error } = await supabase.rpc('recibir_balance_con_cuota_iglesia', {
      p_notificacion_id: id,
    });
    if (error) throw error;
    if (data?.length !== 1) throw new Error('La recepción no devolvió resultado');

    const notificacion = await this.getById(id);
    if (!notificacion) throw new Error('La rendición recibida no se encontró');

    await this.avisarRecepcionAlRemitente(notificacion);

    const [resultado] = data;
    return {
      notificacion,
      montoAJAL: Number(resultado.monto_ajal),
      montoPA: Number(resultado.monto_pa),
      montoRegular: Number(resultado.monto_regular),
      montoPAConfirmados: Number(resultado.monto_pa_confirmados),
      movimientosCreados: resultado.movimientos_creados,
    };
  },

  /**
   * Aviso push al remitente de una rendición ya recibida. La recepción quedó registrada en la
   * base: si el aviso falla, se reporta, pero no se presenta como fallo de la recepción.
   */
  async avisarRecepcionAlRemitente(notificacion: NotificacionBalance): Promise<void> {
    try {
      const entidadNombre = await this.getEntidadNombre(notificacion.tipo_entidad, notificacion.entidad_id);
      await notificacionesService.crear({
        usuario_id: notificacion.enviado_por_usuario_id,
        tipo: 'balance_recibido',
        titulo: 'Balance confirmado como recibido',
        cuerpo: `Su balance de ${this.getNombreMes(notificacion.periodo_mes)} ${notificacion.periodo_anio} para ${entidadNombre} ha sido confirmado como recibido`,
        referencia_id: notificacion.id,
        referencia_tipo: 'notificacion_balance',
      });
    } catch (e: unknown) {
      reportarErrorInesperado(e, 'El balance quedó recibido, pero no se pudo avisar a quien lo envió.', {
        notificacionId: notificacion.id,
      });
    }
  },

  /**
   * Obtener nombre del mes en español.
   */
  getNombreMes(mes: number): string {
    const meses = [
      'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ];
    return meses[mes - 1] || '';
  },
};
